namespace $.$$ {
	const parallel = 3
	const attempts = 3

	type Phase = 'idle' | 'uploading' | 'failed' | 'quota' | 'done'

	type Session = {
		recordingId: number
		partBytes: number
		partCount: number
	}

	function graphql() {
		return $mol_wire_async({
			start: $easyreels_user_upload_start,
			urls: $easyreels_user_upload_urls,
			complete: $easyreels_user_upload_complete,
		})
	}

	export class $easyreels_user_upload extends $.$easyreels_user_upload {
		file: File | null = null
		session: Session | null = null
		etags = new Map<number, string>()

		@$mol_mem
		phase(next?: Phase) {
			return next ?? 'idle'
		}

		@$mol_mem
		parts_done(next?: number) {
			return next ?? 0
		}

		@$mol_mem
		note(next?: $easyreels_i18n_note | null) {
			return next ?? null
		}

		override message() {
			return this.note()?.text() ?? ''
		}

		override detail() {
			return this.note()?.detail ?? ''
		}

		say(key: string, params?: () => $easyreels_i18n_params) {
			this.note($easyreels_i18n_say(`$easyreels_user_upload_${key}`, params))
		}

		override ratio() {
			const done = this.parts_done()

			return this.session ? done / this.session.partCount : 0
		}

		override content() {
			switch (this.phase()) {
				case 'uploading':
					return [this.Bar(), ...this.lines()]
				case 'failed':
					return [this.Bar(), ...this.lines(), this.Retry_button()]
				case 'idle':
					return [this.Pick()]
				default:
					return [this.Pick(), ...this.lines()]
			}
		}

		lines() {
			return this.detail() ? [this.Message(), this.Detail()] : [this.Message()]
		}

		override files(next?: readonly File[]) {
			if (next?.[0]) void this.begin(next[0])

			return next ?? []
		}

		override retry(next?: null) {
			if (next === undefined) return null

			void (this.session ? this.send_missing() : this.begin(this.file!))

			return null
		}

		async begin(file: File) {
			this.file = file
			this.session = null
			this.etags = new Map()
			this.parts_done(0)
			this.phase('uploading')
			this.say('starting')

			try {
				const started = (
					await graphql().start({
						input: { fileName: file.name, mime: file.type || 'application/octet-stream', bytes: file.size },
					})
				).startFileUpload

				if (started.__typename === 'QuotaExceededPayload') {
					this.phase('quota')
					this.say('quota', () => ({
						used: $easyreels_i18n_bytes(started.usedBytes),
						limit: $easyreels_i18n_bytes(started.limitBytes),
					}))
					return
				}

				if (started.__typename === 'ErrorPayload') {
					this.fail($easyreels_i18n_server(started.message))
					return
				}

				this.session = {
					recordingId: started.recording.id,
					partBytes: started.partBytes,
					partCount: started.partCount,
				}
			} catch (error) {
				this.fail($easyreels_i18n_failure(error))
				return
			}

			await this.send_missing()
		}

		fail(note: $easyreels_i18n_note) {
			this.phase('failed')
			this.note(note)
		}

		async send_missing() {
			const session = this.session!
			const queue: number[] = []

			for (let part = 1; part <= session.partCount; part++) {
				if (!this.etags.has(part)) queue.push(part)
			}

			this.phase('uploading')
			this.say('sending')

			const workers = Array.from({ length: parallel }, async () => {
				for (let part = queue.shift(); part !== undefined; part = queue.shift()) {
					await this.send_part(session, part)
				}
			})

			await Promise.all(workers)

			if (this.etags.size < session.partCount) {
				const missing = session.partCount - this.etags.size
				this.fail($easyreels_i18n_say('$easyreels_user_upload_parts_failed', () => ({ count: missing })))
				return
			}

			await this.finish(session)
		}

		async send_part(session: Session, part: number) {
			const from = (part - 1) * session.partBytes
			const body = this.file!.slice(from, from + session.partBytes)

			for (let attempt = 1; attempt <= attempts; attempt++) {
				try {
					const signed = (
						await graphql().urls({
							input: { recordingId: session.recordingId, partNumbers: [part] },
						})
					).requestUploadPartURLs

					if (signed.__typename === 'ErrorPayload') throw new Error(signed.message)

					const response = await fetch(signed.parts[0].url, { method: 'PUT', body })
					if (!response.ok) throw new Error(`HTTP ${response.status}`)

					const etag = response.headers.get('ETag')
					if (!etag) throw new Error('No ETag')

					this.etags.set(part, etag)
					this.parts_done(this.etags.size)
					this.say('progress', () => ({ done: this.etags.size, total: session.partCount }))
					return
				} catch {
					if (attempt === attempts) return
				}
			}
		}

		async finish(session: Session) {
			const parts = [...this.etags.entries()]
				.sort(([left], [right]) => left - right)
				.map(([partNumber, etag]) => ({ partNumber, etag }))

			try {
				const done = (
					await graphql().complete({ input: { recordingId: session.recordingId, parts } })
				).completeFileUpload

				if (done.__typename === 'ErrorPayload') {
					this.fail($easyreels_i18n_server(done.message))
					return
				}
			} catch (error) {
				this.fail($easyreels_i18n_failure(error))
				return
			}

			this.phase('done')
			this.say('done')
		}
	}
}
